import React, { useState, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import { CircularProgress } from "@mui/material";
import AxiosInstance from "../../../utils/AxiosInstance";
import { useShowAlert } from "../../../context/AlertContext";
import { usePermission } from "../../../context/PermissionContext";
import { tax } from "@heartinz/finance";
import { DeleteIcon, DownloadIcon, UploadIcon, XIcon } from "../../../constants/Icons";

const requiredColumns = [
  "Name",
  "Nickname",
  "Phone",
  "Email",
  "Address Line 1",
  "Address Line 2",
  "District",
  "State",
  "Country",
  "Pincode",
  "GST Number",
  "PAN Number",
  "Trade Name",

  // 🔥 NEW FIELDS
  "Website",
  "Contact Person Name",
  "Contact Person Number",

  "Shipping Address Line 1",
  "Shipping Address Line 2",
  "Shipping District",
  "Shipping City",
  "Shipping State",
  "Shipping Country",
  "Shipping Pincode",
];

/* ---------------- VALIDATION FUNCTION ---------------- */

const validateCustomerRow = (row, phoneSet, gstSet, panSet) => {
  const errors = {};

  const phoneRegex = /^[0-9]{10}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const pincodeRegex = /^\d{6}$/;
  const gstRegex =
    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/;
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  const urlRegex =
    /^(https?:\/\/)(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/;

  /* ---------------- PHONE (MANDATORY) ---------------- */

  const phone = String(row["Phone"] || "").trim();

  if (!phone) {
    errors.phone = "Phone number is required";
  } else if (!phoneRegex.test(phone)) {
    errors.phone = "Valid 10-digit mobile number required";
  } else if (phoneSet.has(phone)) {
    errors.phone = "Phone must be unique";
  } else {
    phoneSet.add(phone);
  }

  /* ---------------- OPTIONAL FIELDS ---------------- */

  if (row["Nickname"] && row["Nickname"].length > 50) {
    errors.nickname = "Nickname cannot exceed 50 characters";
  }

  // Email
  if (row["Email"] && !emailRegex.test(row["Email"])) {
    errors.email = "Invalid email format";
  }

  // Pincode
  if (row["Pincode"] && !pincodeRegex.test(String(row["Pincode"]))) {
    errors.pincode = "Pincode must be 6 digits";
  }

  // GST
  if (row["GST Number"]) {
    const gst = String(row["GST Number"]).toUpperCase();
    row["GST Number"] = gst;

    if (!gstRegex.test(gst)) {
      errors.gst = "Invalid GST format";
    } else if (gstSet.has(gst)) {
      errors.gst = "GST must be unique";
    } else {
      gstSet.add(gst);
    }
  }

  // PAN
  if (row["PAN Number"]) {
    const pan = String(row["PAN Number"]).toUpperCase();
    row["PAN Number"] = pan;

    if (!panRegex.test(pan)) {
      errors.pan = "Invalid PAN format";
    } else if (panSet.has(pan)) {
      errors.pan = "PAN must be unique";
    } else {
      panSet.add(pan);
    }
  }

  // Website
  if (row["Website"]) {
    const website = row["Website"].trim();

    if (!urlRegex.test(website)) {
      errors.website =
        "Website must be a valid URL (example: https://www.test.com)";
    }
  }

  // Contact Person Number
  if (
    row["Contact Person Number"] &&
    !phoneRegex.test(String(row["Contact Person Number"]))
  ) {
    errors.contactPersonNumber = "Invalid contact person number";
  }

  return Object.keys(errors).length ? errors : null;
};

/* ---------------- COMPONENT ---------------- */

const CustomerExcelUploader = ({ onDone }) => {
  const { stateList } = usePermission();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [validRows, setValidRows] = useState([]);
  const [duplicateRows, setDuplicateRows] = useState([]);
  const [errorRows, setErrorRows] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const showAlert = useShowAlert();
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const fileInputRef = useRef(null);
  const [duplicateErrorModal, setDuplicateErrorModal] = useState(false);
  const [serverDuplicates, setServerDuplicates] = useState([]);
  const [unuploadedRows, setUnuploadedRows] = useState([]);
  const [showUnuploadedModal, setShowUnuploadedModal] = useState(false);
  const [uploadStats, setUploadStats] = useState({
    total: 0,
    processed: 0, // ✅ NEW
    processing: 0,
    uploaded: 0,
    duplicate: 0,
    error: 0,
    unuploaded: 0, // ✅ NEW
  });

  const getStateNameFromGSTCode = (gstCode) => {
    const state = stateList?.find(
      (item) => String(item.gst_code) === String(gstCode),
    );
    return state?.name || "";
  };

  /* ---------------- SAMPLE DOWNLOAD ---------------- */
  const downloadSampleExcel = () => {
    const sampleData = [
      { Phone: "9876543210" },
      { Phone: "9876543211" },
      { Phone: "9876543212" },
      { Phone: "9876543213" },
      { Phone: "9876543214" },
      { Phone: "9876543215" },
      { Phone: "9876543216" },
      { Phone: "9876543217" },
      {
        Nickname: "Ravi",
        Phone: "9876543218",
        Email: "customer@test.com",
        State: "Tamil Nadu",
        "GST Number": "33ABCDE1234F1Z5",
        "PAN Number": "ABCDE1234F",
      },
      {
        Phone: "9876543219",
        State: "Karnataka",
      },
      {
        Phone: "9876543218",
        Email: "customer@test.com",
        State: "Tamil Nadu",
        "GST Number": "33ABCDE1234F1Z5",
        "PAN Number": "ABCDE1234F",
        Website: "https://www.test.com",
        "Contact Person Name": "Ravi",
        "Contact Person Number": "9876543210",
        "Shipping Address Line 1": "Street 2",
        "Shipping District": "Chennai",
        "Shipping City": "Chennai",
        "Shipping State": "Tamil Nadu",
        "Shipping Country": "India",
        "Shipping Pincode": "600001",
      },
    ];

    // Ensure all required columns exist
    const formattedData = sampleData.map((row) => {
      const fullRow = {};
      requiredColumns.forEach((col) => {
        fullRow[col] = row[col] || "";
      });
      return fullRow;
    });

    // Add empty row gap
    formattedData.push({});
    formattedData.push({});

    // Add rules section
    formattedData.push({
      Name: "===== VALIDATION RULES =====",
    });

    formattedData.push({
      Phone: "Phone → Mandatory, 10 digits, starts with 6-9, must be unique",
    });

    formattedData.push({
      Email: "Email → Optional, must be valid format (example@test.com)",
    });

    formattedData.push({
      State:
        "State → Must be correct Indian state name (Tamil Nadu, Karnataka, etc.)",
    });

    formattedData.push({
      Pincode: "Pincode → Optional, if given must be 6 digits",
    });

    formattedData.push({
      "GST Number":
        "GST → Optional, valid GST format & must be unique (33ABCDE1234F1Z5)",
    });

    formattedData.push({
      "PAN Number": "PAN → Optional, valid PAN format (ABCDE1234F)",
    });

    // ✅ NEW WEBSITE VALIDATION RULE
    formattedData.push({
      Website:
        "Website → Optional, must be valid URL (https://example.com or http://example.com)",
    });

    const ws = XLSX.utils.json_to_sheet(formattedData, {
      header: requiredColumns,
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Customer_Upload_Template");

    XLSX.writeFile(wb, "sample_customer_upload.xlsx");
  };

  /* ---------------- FILE UPLOAD ---------------- */

  const toSafeString = (value) => {
    if (value === undefined || value === null) return "";
    return String(value).trim();
  };

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    setErrors([]);
    setValidRows([]);
    setErrorRows([]);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, {
        type: "array",
        raw: false,
      });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];

      // 🔥 Normalize function (VERY IMPORTANT)
      const normalizeHeader = (value) => {
        return String(value || "")
          .replace(/\u00A0/g, " ") // remove non-breaking space
          .replace(/\s+/g, " ") // collapse multiple spaces
          .trim()
          .toLowerCase();
      };

      // 🔥 Read header row
      const headerRow = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        raw: false,
      })[0];

      if (!headerRow || headerRow.length === 0) {
        setErrors(["Excel file is empty"]);
        setLoading(false);
        return;
      }

      // 🔥 Normalize uploaded headers
      const normalizedUploadedHeaders = headerRow.map(normalizeHeader);

      // 🔥 Normalize required headers
      const normalizedRequiredHeaders = requiredColumns.map(normalizeHeader);

      // 🔥 Find missing
      const missingColumns = normalizedRequiredHeaders.filter(
        (col) => !normalizedUploadedHeaders.includes(col),
      );

      // 🔥 Find extra
      const extraColumns = normalizedUploadedHeaders.filter(
        (col) => col && !normalizedRequiredHeaders.includes(col),
      );

      if (missingColumns.length > 0) {
        setErrors([`Missing required column(s): ${missingColumns.join(", ")}`]);
        setLoading(false);
        return;
      }

      if (extraColumns.length > 0) {
        setErrors([`Invalid column(s) found: ${extraColumns.join(", ")}`]);
        setLoading(false);
        return;
      }

      // 🔥 Now read actual data
      const rows = XLSX.utils.sheet_to_json(sheet, {
        raw: false,
        defval: "",
      });

      const valid = [];
      const invalid = [];
      const phoneSet = new Set();
      const gstSet = new Set();
      const panSet = new Set();

      rows.forEach((row, rowIndex) => {
        // 🔥 Convert every column to string first
        const formattedRow = {};
        requiredColumns.forEach((col) => {
          formattedRow[col] = toSafeString(row[col]);
        });

        let panNumber = formattedRow["PAN Number"];
        let stateName = formattedRow["State"];

        if (formattedRow["GST Number"]) {
          try {
            const gstInfo = tax.india.parseGST(formattedRow["GST Number"]);
            if (gstInfo) {
              // PAN from GST (only if library returns one)
              if (gstInfo.pan) {
                panNumber = gstInfo.pan;
              }

              // State from GST Code
              if (gstInfo.stateCode) {
                const matchedState = getStateNameFromGSTCode(gstInfo.stateCode);

                if (matchedState) {
                  stateName = matchedState;
                }
              }
            }
          } catch (e) {
            console.error(e);
          }
        }

        const rowError = validateCustomerRow(
          formattedRow,
          phoneSet,
          gstSet,
          panSet,
        );

        if (rowError) {
          invalid.push({ ...formattedRow, __errors: rowError });
        } else {
          const contactName = formattedRow["Contact Person Name"];
          const contactNumber = formattedRow["Contact Person Number"];

          const customerPayload = {
            rowId: crypto.randomUUID(),
            excelRowNumber: rowIndex + 1,
            name: formattedRow["Name"],
            nickname: formattedRow["Nickname"],
            phone: formattedRow["Phone"],
            email: formattedRow["Email"],
            addressline1: formattedRow["Address Line 1"],
            addressline2: formattedRow["Address Line 2"],
            district: formattedRow["District"],
            state: stateName,
            country: formattedRow["Country"],
            pincode: formattedRow["Pincode"],
            gstnumber: formattedRow["GST Number"],
            pannumber: panNumber,
            tradeName: formattedRow["Trade Name"],
            website: formattedRow["Website"],

            shippingAddress: {
              addressline1: formattedRow["Shipping Address Line 1"],
              addressline2: formattedRow["Shipping Address Line 2"],
              district: formattedRow["Shipping District"],
              city: formattedRow["Shipping City"],
              state: formattedRow["Shipping State"],
              country: formattedRow["Shipping Country"],
              pincode: formattedRow["Shipping Pincode"],
            },
          };

          // 🔥 build contact person object dynamically
          const contactPerson = {};

          if (contactName) {
            contactPerson.name = contactName;
          }

          if (contactNumber) {
            contactPerson.number = contactNumber;
          }

          // only attach if something exists
          if (Object.keys(contactPerson).length > 0) {
            customerPayload.contactPerson = contactPerson;
          }

          valid.push(customerPayload);
        }
      });

      setValidRows(valid);
      setErrorRows(invalid);
    } catch (err) {
      console.error(err);
      setErrors(["Failed to process file"]);
    }

    setLoading(false);
  }, []);

  const chunkArray = (array, size) => {
    const result = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  };

  /* ---------------- SUBMIT ---------------- */

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const flattenErrors = (errorObj, prefix = "", result = {}) => {
    if (!errorObj) return result;

    Object.keys(errorObj).forEach((key) => {
      if (key === "_errors") {
        const field = prefix.replace(/\.$/, "");
        result[field] = errorObj[key].join(", ");
        return;
      }

      const newPrefix = prefix ? `${prefix}${key}.` : `${key}.`;
      flattenErrors(errorObj[key], newPrefix, result);
    });

    return result;
  };

  const handleSubmit = async () => {
    if (!validRows.length) return;

    try {
      setLoading(true);
      setUploadModal(true);

      const BATCH_SIZE = 200;
      const seenRowIds = new Set();
      let uploadedRowIds = new Set();

      setUploadStats({
        total: validRows.length,
        processing: 0,
        processed: 0,
        uploaded: 0,
        duplicate: 0,
        error: 0,
        unuploaded: 0,
      });

      // If customer transformation is needed, do it here
      const transformedCustomers = validRows.map((customer) => ({
        ...customer,
      }));

      const batches = chunkArray(transformedCustomers, BATCH_SIZE);

      let duplicateAccumulator = [];
      let errorAccumulator = [];

      for (let i = 0; i < batches.length; i++) {
        const currentBatch = batches[i];

        setUploadStats((prev) => ({
          ...prev,
          processing: currentBatch.length,
        }));

        try {
          const res = await AxiosInstance.post("module/customer/bulk", {
            customers: currentBatch,
          });

          const insertedCount = res?.data?.results?.insertedCustomersCount || 0;

          if (insertedCount > 0) {
            currentBatch.forEach((row) => {
              uploadedRowIds.add(row.rowId);
            });
          }
          setUploadStats((prev) => {
            const newUploaded = prev.uploaded + insertedCount;
            const newProcessed = prev.processed + currentBatch.length;

            return {
              ...prev,
              uploaded: newUploaded,
              processed: newProcessed,
              unuploaded: prev.total - newProcessed,
            };
          });
        } catch (err) {
          if (err?.response?.status === 409) {
            const { gstDuplicateNos = [], phoneDuplicateNos = [] } =
              err?.response?.data?.results || {};

            const uniqueRowNos = [
              ...new Set([...gstDuplicateNos, ...phoneDuplicateNos]),
            ];

            // Handle duplicates
            uniqueRowNos.forEach((rowNo) => {
              const index = Number(rowNo) - 1; // batch relative index
              const row = currentBatch[index];

              if (row && !seenRowIds.has(row.rowId)) {
                seenRowIds.add(row.rowId);
                duplicateAccumulator.push(row);
              }
            });

            setUploadStats((prev) => {
              const newDuplicate = prev.duplicate + uniqueRowNos.length;
              const newProcessed = prev.processed + currentBatch.length;

              return {
                ...prev,
                duplicate: newDuplicate,
                processed: newProcessed,
                unuploaded: prev.total - newProcessed,
              };
            });
          } else if (err?.response?.status === 422) {
            const serverErrors = err?.response?.data?.results?.customers || {};

            Object.keys(serverErrors).forEach((key) => {
              if (key === "_errors") return;

              const index = Number(key);
              const row = currentBatch[index];

              if (!row) return;

              const flattened = flattenErrors(serverErrors[key]);

              errorAccumulator.push({
                ...row,
                __errors: flattened,
              });
            });

            setUploadStats((prev) => {
              const newError = prev.error + Object.keys(serverErrors).length;
              const newProcessed = prev.processed + currentBatch.length;

              return {
                ...prev,
                error: newError,
                processed: newProcessed,
                unuploaded: prev.total - newProcessed,
              };
            });
          } else {
            errorAccumulator.push(...currentBatch);

            setUploadStats((prev) => {
              const newError = prev.error + currentBatch.length;
              const newProcessed = prev.processed + currentBatch.length;

              return {
                ...prev,
                error: newError,
                processed: newProcessed,
                unuploaded: prev.total - newProcessed,
              };
            });
          }
        }

        // setUploadStats((prev) => ({
        //   ...prev,
        //   processing: 0,
        // }));
        if (i < batches.length - 1) {
          await delay(2000);
        }
      }
      // ✅ Save duplicates like product logic
      if (duplicateAccumulator.length > 0) {
        setServerDuplicates(
          duplicateAccumulator.map((item) => ({
            rowId: item.rowId, // ✅ VERY IMPORTANT
            data: item,
            fields: ["GST Number", "Phone"],
          })),
        );
      }

      // ✅ Save errors
      if (errorAccumulator.length > 0) {
        setErrorRows(
          errorAccumulator.map((item) => ({
            ...item,
            __errors: { server: "Upload failed" },
          })),
        );
      }

      // ✅ Build Unuploaded Rows (Valid but not uploaded)
      // Get duplicate rowIds
      const duplicateRowIds = new Set(
        duplicateAccumulator.map((row) => row.rowId),
      );

      // Get error rowIds
      const errorRowIds = new Set(errorAccumulator.map((row) => row.rowId));

      // ✅ Only valid & not uploaded & not duplicate & not error
      const unuploaded = validRows.filter(
        (row) =>
          !uploadedRowIds.has(row.rowId) &&
          !duplicateRowIds.has(row.rowId) &&
          !errorRowIds.has(row.rowId),
      );

      setUnuploadedRows(unuploaded);

      setUploadStats((prev) => ({
        ...prev,
        unuploaded: unuploaded.length,
      }));

      if (duplicateAccumulator.length === 0 && errorAccumulator.length === 0) {
        setValidRows([]);
      }
    } catch (error) {
      showAlert("error", "Bulk upload failed");
    } finally {
      setLoading(false);
    }
  };

  const removeDuplicateRow = (rowIdToRemove) => {
    const updatedValidRows = validRows.filter(
      (row) => row.rowId !== rowIdToRemove,
    );

    const updatedServerDuplicates = serverDuplicates.filter(
      (dup) => dup.rowId !== rowIdToRemove,
    );

    setValidRows(updatedValidRows);
    setServerDuplicates(updatedServerDuplicates);

    setUploadStats((prev) => ({
      ...prev,
      total: updatedValidRows.length,
      processed: updatedValidRows.length,
      duplicate: updatedServerDuplicates.length,
    }));

    if (updatedServerDuplicates.length === 0) {
      setDuplicateErrorModal(false);
    }
  };
  /* ---------------- DOWNLOAD ERRORS ---------------- */

  const downloadErrors = () => {
    if (!errorRows.length) return;

    const cleanedRows = errorRows.map(({ __errors, ...rest }) => rest);

    const ws = XLSX.utils.json_to_sheet(cleanedRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Errors");
    XLSX.writeFile(wb, "customer_error_rows.xlsx");
  };

  const downloadUnuploaded = () => {
    if (!unuploadedRows.length) return;

    const formatted = unuploadedRows.map((row) => ({
      Name: row.name || "",
      Nickname: row.nickname || "",
      Phone: row.phone || "",
      Email: row.email || "",
      "Address Line 1": row.addressline1 || "",
      "Address Line 2": row.addressline2 || "",
      District: row.district || "",
      State: row.state || "",
      Country: row.country || "",
      Pincode: row.pincode || "",
      "GST Number": row.gstnumber || "",
      "PAN Number": row.pannumber || "",
      "Trade Name": row.tradeName || "",
      Website: row.website || "",
      "Contact Person Name": row.contactPerson?.name || "",
      "Contact Person Number": row.contactPerson?.number || "",
      "Shipping Address Line 1": row.shippingAddress?.addressline1 || "",
      "Shipping Address Line 2": row.shippingAddress?.addressline2 || "",
      "Shipping District": row.shippingAddress?.district || "",
      "Shipping City": row.shippingAddress?.city || "",
      "Shipping State": row.shippingAddress?.state || "",
      "Shipping Country": row.shippingAddress?.country || "",
      "Shipping Pincode": row.shippingAddress?.pincode || "",
    }));

    const ws = XLSX.utils.json_to_sheet(formatted, {
      header: requiredColumns,
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Unuploaded_Customers");

    XLSX.writeFile(wb, "unuploaded_customers.xlsx");
  };

  const downloadDuplicates = () => {
    if (!serverDuplicates.length) return;

    // Convert back to full Excel format using requiredColumns
    const formattedDuplicates = serverDuplicates.map((dup) => {
      const row = dup.data || {};

      return {
        Name: row.name || "",
        Nickname: row.nickname || "",
        Phone: row.phone || "",
        Email: row.email || "",
        "Address Line 1": row.addressline1 || "",
        "Address Line 2": row.addressline2 || "",
        District: row.district || "",
        State: row.state || "",
        Country: row.country || "",
        Pincode: row.pincode || "",
        "GST Number": row.gstnumber || "",
        "PAN Number": row.pannumber || "",
        "Trade Name": row.tradeName || "",
        Website: row.website || "",
        "Contact Person Name": row.contactPerson?.name || "",
        "Contact Person Number": row.contactPerson?.number || "",
        "Shipping Address Line 1": row.shippingAddress?.addressline1 || "",
        "Shipping Address Line 2": row.shippingAddress?.addressline2 || "",
        "Shipping District": row.shippingAddress?.district || "",
        "Shipping City": row.shippingAddress?.city || "",
        "Shipping State": row.shippingAddress?.state || "",
        "Shipping Country": row.shippingAddress?.country || "",
        "Shipping Pincode": row.shippingAddress?.pincode || "",
      };
    });

    const ws = XLSX.utils.json_to_sheet(formattedDuplicates, {
      header: requiredColumns,
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Duplicate_Customers");

    XLSX.writeFile(wb, "duplicate_customers.xlsx");
  };

  const removeAllDuplicates = () => {
    if (!serverDuplicates.length) return;

    const duplicateRowIds = new Set(serverDuplicates.map((dup) => dup.rowId));

    const updatedValidRows = validRows.filter(
      (row) => !duplicateRowIds.has(row.rowId),
    );

    setValidRows(updatedValidRows);
    setServerDuplicates([]);

    setUploadStats((prev) => ({
      ...prev,
      total: updatedValidRows.length,
      processed: updatedValidRows.length,
      duplicate: 0,
    }));

    setDuplicateErrorModal(false);
  };

  const totalCustomers = validRows.length + errorRows.length;

  const resetAll = () => {
    setValidRows([]);
    setErrorRows([]);
    setDuplicateRows([]);
    setUploadCompleted(false);
    setUploadSummary(null);
    setShowErrorModal(false);
    setUploadStats({
      total: 0,
      processed: 0,
      processing: 0,
      uploaded: 0,
      duplicate: 0,
      error: 0,
    });
    if (serverDuplicates?.length === 0) {
      handleSubmit();
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onDone?.();
  };

  const clearAll = () => {
    setValidRows([]);
    setErrorRows([]);
    setDuplicateRows([]);
    setUploadCompleted(false);
    setUploadSummary(null);
    setShowErrorModal(false);
    setUploadStats({
      total: 0,
      processed: 0,
      processing: 0,
      uploaded: 0,
      duplicate: 0,
      error: 0,
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="page-wrapper">
      <div className="page-content">
        <div className="card">
          <div className="card-body">
            <h4>Bulk Customer Upload</h4>

            <label className="form-label mt-2">
              Upload Excel File{" "}
              <small
                style={{ cursor: "pointer", color: "blue" }}
                onClick={downloadSampleExcel}
              >
                (Download Sample)
              </small>
            </label>

            <div className="col-lg-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                className="form-control"
              />
            </div>

            {/* SUMMARY SECTION */}
            {(validRows.length > 0 || errorRows.length > 0) && (
              <div className="mt-3 p-3 border rounded bg-light d-flex  align-items-center flex-wrap">
                {/* LEFT SIDE - COUNTS */}
                <div>
                  <span className="me-3 fw-bold">
                    Total: {totalCustomers} Customers
                  </span>

                  <span className="me-3 text-success fw-bold">
                    Valid Customer: {validRows.length}
                  </span>
                </div>

                {/* RIGHT SIDE - ACTION BUTTONS */}
                <div className="d-flex gap-2 mt-2 mt-md-0 align-items-center">
                  {validRows.length > 0 && (
                    <>
                      {loading ? (
                        <button className="btn btn-sm btn-primary" disabled>
                          <CircularProgress size={22} className="text-white" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={handleSubmit}
                          disabled={loading || uploadCompleted}
                        >
                          <UploadIcon/> Upload Valid
                        </button>
                      )}
                    </>
                  )}

                  {errorRows.length > 0 && (
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => setShowErrorModal(true)}
                    >
                      View Errors ({errorRows.length})
                    </button>
                  )}
                </div>
                <div className="ms-auto">
                  <button
                    className="btn btn-outline-secondary btn-sm text-right"
                    onClick={clearAll}
                  >
                    <XIcon/> Clear
                  </button>
                </div>
              </div>
            )}

            {/* {loading && <CircularProgress size={25} className="mt-2" />} */}

            {errors.length > 0 && (
              <div className="text-danger mt-2">
                {errors.map((err, i) => (
                  <div key={i}>{err}</div>
                ))}
              </div>
            )}

            {validRows.length > 0 && (
              <div className="mt-3">
                <h6 className="text-gray">
                  Valid Customer ({validRows.length})
                </h6>
                <div className="row">
                  <div className="col-lg-12 table-responsive rtable ">
                    <table className="table table-sm table-bordered mb-0 rtable">
                      <thead className="bg-light">
                        <tr>
                          {requiredColumns.map((col, i) => (
                            <th key={i}>{col}</th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {validRows.map((row, i) => (
                          <tr key={i}>
                            <td>{row.name}</td>
                            <td>{row.nickname}</td>
                            <td>{row.phone}</td>
                            <td>{row.email}</td>
                            <td>{row.addressline1}</td>
                            <td>{row.addressline2}</td>
                            <td>{row.district}</td>
                            <td>{row.state}</td>
                            <td>{row.country}</td>
                            <td>{row.pincode}</td>
                            <td>{row.gstnumber}</td>
                            <td>{row.pannumber}</td>
                            <td>{row.tradeName}</td>
                            <td>{row.website}</td>
                            <td>{row.contactPerson?.name}</td>
                            <td>{row.contactPerson?.number}</td>
                            <td>{row.shippingAddress?.addressline1}</td>
                            <td>{row.shippingAddress?.addressline2}</td>
                            <td>{row.shippingAddress?.district}</td>
                            <td>{row.shippingAddress?.city}</td>
                            <td>{row.shippingAddress?.state}</td>
                            <td>{row.shippingAddress?.country}</td>
                            <td>{row.shippingAddress?.pincode}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* {duplicateRows.length > 0 && (
              <div className="mt-3">
                <h6 className="text-warning">
                  Duplicate Customer ({duplicateRows.length})
                </h6>

                <div className="row">
                  <div className="col-lg-12 table-responsive rtable">
                    <table className="table table-sm table-bordered mb-0 rtable">
                      <thead className="bg-light">
                        <tr>
                          {requiredColumns.map((col, i) => (
                            <th key={i}>{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {duplicateRows.filter(Boolean).map((row, i) => (
                          <tr key={i} className="table-warning">
                            <td>{row.name}</td>
                            <td>{row.phone}</td>
                            <td>{row.email}</td>
                            <td>{row.addressline1}</td>
                            <td>{row.addressline2}</td>
                            <td>{row.district}</td>
                            <td>{row.state}</td>
                            <td>{row.country}</td>
                            <td>{row.pincode}</td>
                            <td>{row.gstnumber}</td>
                            <td>{row.pannumber}</td>
                            <td>{row.tradeName}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* <div className="modal-footer d-block mt-3">
              {loading ? (
                <button className="btn btn-sm btn-primary" disabled>
                  <CircularProgress size={22} className="text-white" />
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  <i className="bx bx-check"></i> Submit
                </button>
              )}

              {errorRows.length > 0 && (
                <button
                  className="btn btn-danger btn-sm text-right"
                  onClick={() => setShowErrorModal(true)}
                >
                  View Errors ({errorRows.length})
                </button>
              )}
            </div> */}
          </div>
        </div>

        {/* ERROR MODAL */}
        {showErrorModal && (
          <div>
            <div className="modal-backdrop show"></div>

            <div
              className="modal show d-block"
              id="CustomerModal"
              tabIndex="0"
              aria-modal="true"
              role="dialog"
            >
              <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content">
                  {/* HEADER */}
                  <div className="modal-header d-flex justify-content-between align-items-center">
                    <h6 className="modal-title  mb-0">
                      Error Customer ({errorRows.length})
                    </h6>

                    <div>
                      <button
                        className="btn btn-export btn-sm me-2"
                        onClick={downloadErrors}
                      >
                        <DownloadIcon iClass={"fs-15"}/> Download as
                        Excel
                      </button>

                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowErrorModal(false)}
                      ></button>
                    </div>
                  </div>

                  {/* BODY */}
                  <div className="modal-body">
                    <div className="col-lg-12 table-responsive rtable">
                      <table className="table table-sm table-bordered mb-0">
                        <thead className="bg-light">
                          <tr>
                            {/* ERROR COLUMN FIRST */}
                            <th>Error</th>
                            {requiredColumns.map((col, i) => (
                              <th key={i}>{col}</th>
                            ))}
                          </tr>
                        </thead>

                        <tbody>
                          {errorRows.map((row, i) => (
                            <tr key={i}>
                              {/* ERROR COLUMN FIRST */}
                              <td>
                                {Object.values(row.__errors).map((msg, idx) => (
                                  <div key={idx}>• {msg}</div>
                                ))}
                              </td>

                              {/* DATA COLUMNS */}
                              {requiredColumns.map((col, j) => {
                                const hasError =
                                  row.__errors &&
                                  ((col === "Phone" && row.__errors.phone) ||
                                    (col === "Email" && row.__errors.email) ||
                                    (col === "Pincode" &&
                                      row.__errors.pincode) ||
                                    (col === "GST Number" &&
                                      row.__errors.gst) ||
                                    (col === "PAN Number" &&
                                      row.__errors.pan) ||
                                    (col === "Website" &&
                                      row.__errors.website) ||
                                    (col === "Contact Person Number" &&
                                      row.__errors.contactPersonNumber) ||
                                    (col === "Nickname" &&
                                      row.__errors.nickname));

                                return (
                                  <td
                                    key={j}
                                    style={
                                      hasError
                                        ? {
                                            backgroundColor: "#ffe5e5",
                                            color: "#d8000c",
                                            fontWeight: "500",
                                          }
                                        : {}
                                    }
                                  >
                                    {row[col]}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* {uploadModal && (
          <div>
            <div className="modal-backdrop show"></div>

            <div className="modal show d-block" tabIndex="0">
              <div className="modal-dialog  modal-dialog-scrollable">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Successfully Uploaded</h5>
                    <button
                      className="btn-close"
                      onClick={() => {
                        setUploadModal(false);
                        resetAll();
                      }}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3 text-center text-success">
                      <strong className="text-success">
                        Successfully Uploaded Counts:
                      </strong>{" "}
                      {uploadSummary?.uploaded}
                    </div>
                    {/* SHOW ERROR BUTTON 
                  </div>
                  <div className="modal-footer d-block">
                    {errorRows.length > 0 && (
                      <button
                        className="btn btn-danger btn-sm text-left"
                        onClick={() => {
                          setTimeout(() => {
                            setShowErrorModal(true);
                          }, 200);
                        }}
                      >
                        View Errors ({errorRows.length})
                      </button>
                    )}
                    <button
                      className="btn btn-primary btn-sm text-right"
                      onClick={() => {
                        setUploadModal(false);
                        resetAll();
                      }}
                    >
                      <i className="bx bx-check"></i> Done
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )} */}

        {uploadModal && (
          <>
            <div className="modal-backdrop show custom-backdrop"></div>

            <div className="modal show d-block" tabIndex="0">
              <div className="modal-dialog  modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content">
                  {/* HEADER */}
                  <div className="modal-header border-0">
                    <h5 className="modal-title fw-bold">Bulk Upload Status</h5>
                    <button
                      className="btn-close"
                      onClick={() => setUploadModal(false)}
                    ></button>
                  </div>

                  {/* BODY */}
                  <div className="modal-body">
                    {/* Total */}
                    {/* <div className="text-center mb-4">
                      <h6 className="text-muted mb-1">Total Records</h6>
                      <h2 className="fw-bold">{uploadStats.total}</h2>
                    </div> */}

                    {/* Stats Grid */}
                    <div className="row text-center g-3 mb-4">
                      <div className="col-6">
                        <div className="stat-card total">
                          <h6 className="text-muted mb-1">Total Records</h6>
                          <h4 className="fw-bold">{uploadStats.total}</h4>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          className="stat-card secondary clickable-card"
                          onClick={() => {
                            if (uploadStats.unuploaded > 0) {
                              setShowUnuploadedModal(true);
                            }
                          }}
                          style={{
                            cursor:
                              uploadStats.unuploaded > 0
                                ? "pointer"
                                : "default",
                          }}
                        >
                          <h6>Unuploaded</h6>
                          <h4>{uploadStats.unuploaded}</h4>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="stat-card processing">
                          <h6>Processed</h6>
                          <h4>{uploadStats.processed}</h4>
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="stat-card success">
                          <h6>Uploaded</h6>
                          <h4>{uploadStats.uploaded}</h4>
                        </div>
                      </div>

                      <div className="col-6">
                        <div
                          className="stat-card warning clickable-card"
                          onClick={() => {
                            if (uploadStats.duplicate > 0) {
                              setDuplicateErrorModal(true);
                            }
                          }}
                          style={{
                            cursor:
                              uploadStats.duplicate > 0 ? "pointer" : "default",
                          }}
                        >
                          <h6>View Duplicates</h6>
                          <h4>{uploadStats.duplicate}</h4>
                        </div>
                      </div>

                      <div className="col-6">
                        <div
                          className="stat-card danger clickable-card"
                          onClick={() => {
                            if (uploadStats.error > 0) {
                              setShowErrorModal(true);
                            }
                          }}
                          style={{
                            cursor:
                              uploadStats.error > 0 ? "pointer" : "default",
                          }}
                        >
                          <h6>View Errors</h6>
                          <h4>{uploadStats.error}</h4>
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="progress modern-progress">
                      <div
                        className={`progress-bar ${
                          uploadStats.uploaded + uploadStats.processing <
                          uploadStats.total
                            ? "progress-bar-striped progress-bar-animated"
                            : ""
                        }`}
                        role="progressbar"
                        style={{
                          width: `${
                            uploadStats.total
                              ? (uploadStats.processed / uploadStats.total) *
                                100
                              : 0
                          }%`,
                        }}
                      >
                        {uploadStats.total
                          ? Math.round(
                              (uploadStats.processed / uploadStats.total) * 100,
                            )
                          : 0}
                        %
                      </div>
                    </div>

                    {/* Completed Badge */}
                    {uploadStats.processed >= uploadStats.total && (
                      <div className="text-center mt-3 fw-semibold">
                        {uploadStats.uploaded === uploadStats.total ? (
                          <span className="text-success">
                            ✅ Upload Completed Successfully
                          </span>
                        ) : (
                          <span className="text-warning">
                            ⚠️ Upload Finished (Some Duplicates or Errors Found)
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* FOOTER */}
                  <div className="modal-footer border-0 d-flex ">
                    {/* <div>
                      {uploadStats.duplicate > 0 && (
                        <button
                          className="btn btn-label-danger btn-sm me-2"
                          onClick={() => setDuplicateErrorModal(true)}
                        >
                          View Duplicates ({uploadStats.duplicate})
                        </button>
                      )}

                      {uploadStats.error > 0 && (
                        <button
                          className="btn  btn-label-danger btn-sm"
                          onClick={() => setShowErrorModal(true)}
                        >
                          View Errors ({uploadStats.error})
                        </button>
                      )}
                    </div> */}

                    <button
                      className="btn btn-primary btn-sm text-right"
                      onClick={() => {
                        setUploadModal(false);
                        resetAll();
                      }}
                      disabled={loading}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {showUnuploadedModal && (
          <>
            <div className="modal-backdrop show"></div>

            <div className="modal show d-block" tabIndex="0">
              <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title text-warning">
                      Unuploaded Customers ({unuploadedRows.length})
                    </h5>

                    <div>
                      <button
                        className="btn btn-sm btn-export me-2"
                        onClick={downloadUnuploaded}
                      >
                        <DownloadIcon/> Download as Excel
                      </button>

                      <button
                        className="btn-close"
                        onClick={() => setShowUnuploadedModal(false)}
                      ></button>
                    </div>
                  </div>

                  <div className="modal-body">
                    <table className="table table-bordered table-sm">
                      <thead>
                        <tr>
                          {requiredColumns.map((col, i) => (
                            <th key={i}>{col}</th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {unuploadedRows.map((row, i) => (
                          <tr key={i}>
                            <td>{row.name}</td>
                            <td>{row.nickname}</td>
                            <td>{row.phone}</td>
                            <td>{row.email}</td>
                            <td>{row.addressline1}</td>
                            <td>{row.addressline2}</td>
                            <td>{row.district}</td>
                            <td>{row.state}</td>
                            <td>{row.country}</td>
                            <td>{row.pincode}</td>
                            <td>{row.gstnumber}</td>
                            <td>{row.pannumber}</td>
                            <td>{row.tradeName}</td>
                            <td>{row.website}</td>
                            <td>{row.contactPerson?.name}</td>
                            <td>{row.contactPerson?.number}</td>
                            <td>{row.shippingAddress?.addressline1}</td>
                            <td>{row.shippingAddress?.addressline2}</td>
                            <td>{row.shippingAddress?.district}</td>
                            <td>{row.shippingAddress?.city}</td>
                            <td>{row.shippingAddress?.state}</td>
                            <td>{row.shippingAddress?.country}</td>
                            <td>{row.shippingAddress?.pincode}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="modal-footer">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setShowUnuploadedModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {duplicateErrorModal && (
          <div>
            <div className="modal-backdrop show"></div>

            <div className="modal show d-block" tabIndex="0">
              <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title text-danger">
                      Duplicate Customers ({serverDuplicates.length})
                    </h5>

                    <div>
                      <button
                        className="btn btn-sm btn-export me-2"
                        onClick={downloadDuplicates}
                      >
                        <DownloadIcon/> Download as Excel
                      </button>

                      <button
                        className="btn-close"
                        onClick={() => setDuplicateErrorModal(false)}
                      ></button>
                    </div>
                  </div>

                  <div className="modal-body">
                    {/* <div className="alert alert-danger">
                      {serverDuplicates[0]?.message}
                    </div> */}

                    <table className="table table-bordered table-sm">
                      <thead>
                        <tr>
                          {/* <th>Array Index</th> */}
                          <th>Duplicate Field</th>
                          <th>Name</th>
                          <th>Phone</th>
                          <th>Email</th>
                          <th>GST</th>
                          <th>PAN</th>
                          <th>Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {serverDuplicates.map((dup, i) => {
                          const isPhoneDuplicate = dup.fields.includes("Phone");
                          const isGstDuplicate =
                            dup.fields.includes("GST Number");
                          const isPanDuplicate =
                            dup.fields.includes("PAN Number");

                          return (
                            <tr key={i}>
                              <td>
                                {dup.fields.map((field, idx) => (
                                  <div key={idx}>{field} already exists</div>
                                ))}
                              </td>

                              <td>{dup.data?.name}</td>

                              {/* 🔴 PHONE */}
                              <td
                                style={
                                  isPhoneDuplicate
                                    ? {
                                        backgroundColor: "#ffe5e5",
                                        color: "#d8000c",
                                        fontWeight: "500",
                                      }
                                    : {}
                                }
                              >
                                {dup.data?.phone}
                              </td>

                              <td>{dup.data?.email}</td>

                              {/* 🔴 GST */}
                              <td
                                style={
                                  isGstDuplicate
                                    ? {
                                        backgroundColor: "#ffe5e5",
                                        color: "#d8000c",
                                        fontWeight: "500",
                                      }
                                    : {}
                                }
                              >
                                {dup.data?.gstnumber}
                              </td>

                              {/* 🔴 PAN */}
                              <td
                                style={
                                  isPanDuplicate
                                    ? {
                                        backgroundColor: "#ffe5e5",
                                        color: "#d8000c",
                                        fontWeight: "500",
                                      }
                                    : {}
                                }
                              >
                                {dup.data?.pannumber}
                              </td>

                              <td className="text-center">
                                <button
                                  className="btn btn-sm btn-label-danger shadow-none"
                                  onClick={() => removeDuplicateRow(dup.rowId)}
                                >
                                  <DeleteIcon/>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="modal-footer d-flex justify-content-between">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setDuplicateErrorModal(false)}
                    >
                      <XIcon/> Close
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={removeAllDuplicates}
                    >
                      <XIcon/> Remove All Duplicates
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerExcelUploader;
